import { NavLink, useNavigate } from 'react-router'
import {
  HomeIcon,
  PhoneIcon,
  PlusIcon,
  UsersIcon,
  SettingsIcon,
  CoinsIcon,
  CreditCardIcon,
  KeyRoundIcon,
  BadgeCheckIcon,
  LogOutIcon,
} from 'lucide-react'
import { useState } from 'react'
import { signOut } from 'firebase/auth'
import { useQueryClient } from '@tanstack/react-query'
import { auth, track } from '@/lib/firebase/app'
import { useUser } from '@/lib/firebase/auth'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/shared/Avatar'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { ThemeToggle } from '@/components/layout/ThemeToggle'
import { LiveCallPill } from '@/components/layout/LiveCallPill'
import { getMachineId } from '@/lib/device'
import { api } from '@/lib/api/endpoints'
import { useProfile } from '@/hooks/queries/useProfile'

const primaryNav = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/calls', label: 'Calls', icon: PhoneIcon },
  { to: '/new', label: 'New call', icon: PlusIcon },
  { to: '/contacts', label: 'Contacts', icon: UsersIcon },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
]

const secondaryNav = [
  { to: '/credits', label: 'Credits', icon: CoinsIcon },
  { to: '/subscription', label: 'Subscription', icon: CreditCardIcon },
  { to: '/verification', label: 'Verification', icon: BadgeCheckIcon },
  { to: '/keys', label: 'API keys', icon: KeyRoundIcon },
]

function NavItem({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string
  label: string
  icon: typeof HomeIcon
  end?: boolean
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-sidebar-foreground hover:bg-accent hover:text-accent-foreground',
        )
      }
    >
      <Icon className="size-4.5 shrink-0" />
      {label}
    </NavLink>
  )
}

export function Sidebar() {
  const user = useUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [confirmSignOut, setConfirmSignOut] = useState(false)

  const handleSignOut = async () => {
    track('preferences_logout_confirm')
    try {
      await api.logout(getMachineId())
    } catch {
      // Best-effort device deregistration; sign out regardless.
    }
    await signOut(auth)
    queryClient.clear()
    navigate('/')
  }

  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r bg-sidebar md:flex">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <img src="/icons/icon-96.png" alt="" className="size-8 rounded-lg" />
        <span className="font-display text-lg font-bold tracking-tight">VOYP</span>
      </div>

      <div className="px-3">
        <LiveCallPill />
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-2">
        {primaryNav.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
        <div className="mt-4 mb-1 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          Account
        </div>
        {secondaryNav.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
      </nav>

      <div className="border-t p-3">
        <div className="flex items-center gap-2.5 px-2 pt-1 pb-2">
          <Avatar src={user.photoURL} name={user.displayName || user.email} className="size-8" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user.displayName || user.email}</p>
            <CreditsLine />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-start text-muted-foreground"
            onClick={() => {
              track('preferences_logout')
              setConfirmSignOut(true)
            }}
          >
            <LogOutIcon className="size-4" />
            Sign out
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmSignOut}
        onOpenChange={setConfirmSignOut}
        title="Sign out?"
        description="You can sign back in anytime."
        confirmLabel="Sign out"
        onConfirm={handleSignOut}
      />
    </aside>
  )
}

function CreditsLine() {
  const { data } = useProfile()
  if (data?.user?.credits == null) return null
  return (
    <p className="font-mono text-xs tabular-nums text-muted-foreground">
      {Math.floor(data.user.credits)} credits
    </p>
  )
}
